/**
 * Password policy — the browser-side mirror of
 * `packages/shared/src/crypto/password-policy.ts`, which is the source of truth.
 *
 * The registration form used to advertise a floor of 6 while the server refused
 * anything under 8, so a player who typed exactly what the placeholder asked for
 * got a generic "request validation failed" banner with no field named. The
 * number now comes from one place; this file only restates the rules so a
 * refusal can be shown under the password box instead of after a round trip.
 *
 * The SPA does NOT import the `@b1fr0st/shared` barrel at runtime — it pulls
 * pino, argon2 and ioredis into the bundle, and `lib/money.ts` in the backoffice
 * says the same. (A pure SUBPATH is a different matter: `lib/datetime/duration.ts`
 * takes `@b1fr0st/shared/time`, which reaches none of that.) The policy itself
 * has no subpath to take, so it is mirrored rather than imported, and
 * `password-policy.test.ts` next to this file imports the real module
 * and asserts the two agree over a corpus, so a rule added on the server without
 * a matching one here fails the suite rather than reaching a player.
 */

/** Minimum length for any new password. Pinned to the backend constant. */
export const PASSWORD_MIN_LENGTH = 6;

const DENYLIST = new Set([
  '12345678', '123456789', '1234567890', '123123123', '11111111', '00000000',
  'password', 'password1', 'password123', 'passw0rd', 'p@ssword', 'p@ssw0rd',
  'qwerty123', 'qwertyui', 'abc12345', 'a1234567', 'iloveyou', 'letmein1',
  'football', 'baseball', 'sunshine', 'princess', 'trustno1', 'superman',
  'admin123', 'welcome1', 'welcome123', 'changeme', 'monkey12',
  'b1fr0st', 'bifrost1', 'bifrost123', 'casino123', 'jackpot1',
  '123456', '1234567', '654321', '123123', '111111', '000000', '121212',
  'qwerty', 'qwerty1', 'qwertyu', 'asdfgh', 'zxcvbn', 'abc123', 'a123456',
  'monkey', 'dragon', 'shadow', 'master', 'letmein', 'welcome', 'freedom',
  'iloveu', 'sunshin', 'bifrost', 'casino', 'jackpot', 'lottery',
]);

export type PasswordViolationCode =
  | 'too_short'
  | 'too_common'
  | 'single_character'
  | 'sequential';

/** Every reason a password is unacceptable, in the backend's order. */
export function checkPasswordPolicy(password: string): PasswordViolationCode[] {
  const violations: PasswordViolationCode[] = [];
  const value = password ?? '';

  if (value.length < PASSWORD_MIN_LENGTH) violations.push('too_short');
  if (DENYLIST.has(value.trim().toLowerCase())) violations.push('too_common');
  if (value.length > 0 && new Set(value).size === 1) violations.push('single_character');
  if (isSequential(value)) violations.push('sequential');

  return violations;
}

function isSequential(value: string): boolean {
  if (value.length < PASSWORD_MIN_LENGTH) return false;
  const codes = [...value.toLowerCase()].map((c) => c.charCodeAt(0));
  let ascending = true;
  let descending = true;
  for (let i = 1; i < codes.length; i++) {
    const delta = codes[i]! - codes[i - 1]!;
    if (delta !== 1) ascending = false;
    if (delta !== -1) descending = false;
  }
  return ascending || descending;
}

/** The i18n key for the first thing wrong with a password, or undefined. */
export function passwordErrorKey(password: string): string | undefined {
  const key: Record<PasswordViolationCode, string> = {
    too_short: 'register.errors.passwordTooShort',
    too_common: 'register.errors.passwordTooCommon',
    single_character: 'register.errors.passwordSingleCharacter',
    sequential: 'register.errors.passwordSequential',
  };
  const first = checkPasswordPolicy(password)[0];
  return first ? key[first] : undefined;
}
